
import {
	ModuleContentType,
	ModuleTemplate,
	ObjectType,
	Platform,
	PopularityRankTypes,
	PopularTitlesSorting,
	StreamingChartObjectType,
} from '@/@types/graphql-types';
import type { WebLocale } from '@/enums/web-locale';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import Provider from '@/interfaces/provider';
import { PopularTabType } from '@/interfaces/titles';
import {
	GetProviderTop10TitlesDocument,
	GetProviderTop10TitlesQuery,
	GetProviderTop10TitlesQueryVariables,
} from './queries/GetProviderTop10Titles.query';
import {
	GetProviderTop10TitlesFallbackDocument,
	GetProviderTop10TitlesFallbackQueryVariables,
} from './queries/GetProviderTop10TitlesFallback.query';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Top10HorizontalTitleListGraphql, { HorizontalTitleListItem } from './Top10HorizontalTitleListGraphql.vue';
import { SnowplowModuleContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';

const Language = namespace('language');
const Routing = namespace('routing');

@Component({
	components: { Top10HorizontalTitleListGraphql },
	apollo: {
		top10ProviderMovieTitles: {
			query: GetProviderTop10TitlesDocument,
			fetchPolicy: 'cache-and-network',
			variables(): GetProviderTop10TitlesQueryVariables {
				return {
					after: '',
					watchNowFilter: {
						packages: [this.provider.shortName],
					},
					language: this.language,
					country: this.country,
					first: 10,
					platform: Platform.Web,
					streamingChartsFilter: {
						objectType: StreamingChartObjectType.Movie,
						category: PopularityRankTypes.WeeklyPopularitySameContentType,
						nextTitles: 0,
						previousTitles: 0,
						packages: [this.provider.shortName],
					},
				};
			},
			update(data) {
				this.loaded = true;
				return data.streamingCharts;
			},
			error(e) {
				console.log(e);
			},
		},
		top10ProviderShowTitles: {
			query: GetProviderTop10TitlesDocument,
			fetchPolicy: 'cache-and-network',
			variables(): GetProviderTop10TitlesQueryVariables {
				return {
					after: '',
					watchNowFilter: {
						packages: [this.provider.shortName],
					},
					language: this.language,
					country: this.country,
					first: 10,
					platform: Platform.Web,
					streamingChartsFilter: {
						objectType: StreamingChartObjectType.Show,
						category: PopularityRankTypes.WeeklyPopularitySameContentType,
						nextTitles: 0,
						previousTitles: 0,
						packages: [this.provider.shortName],
					},
				};
			},
			update(data) {
				this.loaded = true;
				return data.streamingCharts;
			},
			error(e) {
				console.log(e);
			},
		},
		top10ProviderTitles: {
			query: GetProviderTop10TitlesDocument,
			fetchPolicy: 'cache-and-network',
			variables(): GetProviderTop10TitlesQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				this.loaded = true;
				return data.streamingCharts;
			},
			error(e) {
				console.log(e);
			},
		},
		top10FallbackTitles: {
			query: GetProviderTop10TitlesFallbackDocument,
			fetchPolicy: 'cache-and-network',
			variables(): GetProviderTop10TitlesFallbackQueryVariables {
				return {
					after: '',
					popularTitlesFilter: {
						packages: [this.provider.shortName],
						objectTypes: this.top10TitlesObjectTypes,
					},
					watchNowFilter: {
						packages: [this.provider.shortName],
					},
					popularTitlesSortBy: PopularTitlesSorting.Trending,
					language: this.language,
					country: this.country,
					first: 10,
					platform: Platform.Web,
					streamingChartsFilter: {
						category: PopularityRankTypes.WeeklyPopularitySameContentType,
						objectType:
							this.top10TitlesObjectTypes.length === 1
								? (this.top10TitlesObjectTypes[0] as any)
								: undefined,
						nextTitles: 0,
						previousTitles: 0,
					},
				};
			},
			update(data) {
				this.loaded = true;
				return data.popularTitles;
			},
			error(e) {
				console.log(e);
			},
		},
	},
})
export default class Top10Titles extends Vue {
	@Prop() provider: Provider;
	@Prop() selectedTab: PopularTabType;
	@Prop({ default: true }) showTitle: boolean;
	@Prop({ default: false }) showMobileTitle: boolean;

	@Language.State webLocale: WebLocale;
	@Language.Getter language: string;
	@Language.Getter country: string;

	@Routing.State activeRoute: any;

	loaded = false;

	top10ProviderMovieTitles: GetProviderTop10TitlesQuery['streamingCharts'] =
		{} as GetProviderTop10TitlesQuery['streamingCharts'];
	top10ProviderShowTitles: GetProviderTop10TitlesQuery['streamingCharts'] =
		{} as GetProviderTop10TitlesQuery['streamingCharts'];
	top10ProviderTitles: GetProviderTop10TitlesQuery['streamingCharts'] =
		{} as GetProviderTop10TitlesQuery['streamingCharts'];

	top10FallbackTitles: any;

	get titlesToDisplay() {
		if (this.titleNodes?.length !== 10) {
			return this.fallbackTitleNodes;
		} else {
			return this.titleNodes;
		}
	}

	get titleNodes() {
		if (this.selectedTab === PopularTabType.MOVIE)
			return this.top10ProviderMovieTitles?.edges?.map((el: { node: any }) => el.node);
		if (this.selectedTab === PopularTabType.SHOW)
			return this.top10ProviderShowTitles?.edges?.map((el: { node: any }) => el.node);
		return this.top10MixedTitles?.map((el: { node: any }) => el.node);
	}

	get fallbackTitleNodes() {
		return this.top10FallbackTitles?.edges
			?.map((el: { node: any }) => el.node)
			.sort(
				(
					a: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } },
					b: { streamingCharts: { edges: { streamingChartInfo: { rank: number } }[] } }
				) => {
					const position_a = a.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
					const position_b = b.streamingCharts?.edges?.[0]?.streamingChartInfo?.rank || 0;
					return position_a - position_b;
				}
			);
	}

	get showNoTitles() {
		return this.titlesToDisplay?.length < 10;
	}

	get top10MixedTitles() {
		const mixedTitles = this.top10ProviderMovieTitles?.edges?.concat(this.top10ProviderShowTitles?.edges || []);

		const sortedMixedTitles = mixedTitles?.sort((a: any, b: any) => {
			if (a?.streamingChartInfo?.rank && b?.streamingChartInfo?.rank) {
				const rankScoreA = a?.streamingChartInfo?.rank * 0.99;
				const rankScoreB = b?.streamingChartInfo?.rank * 0.99;
				const daysInTop10ScoreA = a?.streamingChartInfo?.daysInTop10 * 0.01;
				const daysInTop10ScoreB = b?.streamingChartInfo?.daysInTop10 * 0.01;

				const scoreA = rankScoreA + daysInTop10ScoreA;
				const scoreB = rankScoreB + daysInTop10ScoreB;

				a = {
					...a,
					score: scoreA,
				};

				b = {
					...b,
					score: scoreB,
				};

				return a.score - b.score;
			}
			return 0;
		});

		return sortedMixedTitles?.slice(0, 10);
	}

	get top10TitlesObjectTypes() {
		switch (this.selectedTab) {
			case PopularTabType.MOVIE:
				return [ObjectType.Movie];

			case PopularTabType.SHOW:
				return [ObjectType.Show];

			case PopularTabType.ALL:
				return [ObjectType.Show, ObjectType.Movie];

			default:
				return [];
		}
	}

	get queryVariables(): GetProviderTop10TitlesQueryVariables {
		return {
			after: '',
			watchNowFilter: {
				packages: [this.provider.shortName],
			},
			language: this.language,
			country: this.country,
			first: 10,
			platform: Platform.Web,
			streamingChartsFilter: {
				category: PopularityRankTypes.WeeklyPopularitySameContentType,
				objectType:
					this.top10TitlesObjectTypes.length === 1 ? (this.top10TitlesObjectTypes[0] as any) : undefined,
				nextTitles: 0,
				previousTitles: 0,
				packages: [this.provider.shortName],
			},
		};
	}

	@Watch('queryVariables')
	onQueryVariablesChange() {
		this.loaded = false;
	}

	get titleText() {
		switch (this.selectedTab) {
			case PopularTabType.ALL:
				return this.$t('WEBAPP_PROVIDER_PAGE_TOP_10_TITLE', { provider: this.provider.clearName });
			case PopularTabType.MOVIE:
				return this.$t('WEBAPP_PROVIDER_PAGE_TOP_10_MOVIES', { provider: this.provider.clearName });
			case PopularTabType.SHOW:
				return this.$t('WEBAPP_PROVIDER_PAGE_TOP_10_TV_SHOWS', { provider: this.provider.clearName });
			default:
				return '';
		}
	}

	get h3InnerText() {
		return `Top 10 not available for ${this.provider.clearName}`;
	}

	trackScroll(label: 'right' | 'left') {
		TrackingHelper.trackEvent('userinteraction', {
			action: 'horizontal_scroller_clicked',
		});
	}

	getSelectedTab(routeName: string): PopularTabType | undefined {
		const name = routeName.split('app.titles.popular.provider')[1];
		if (name === undefined) {
			return undefined;
		}
		switch (name) {
			case '-movie':
				return PopularTabType.MOVIE;
			case '-show':
				return PopularTabType.SHOW;
			default:
				return PopularTabType.ALL;
		}
	}

	getModuleContext(moduleTemplate: ModuleTemplate) {
		return new SnowplowModuleContext('', -1, moduleTemplate.technicalName, '', moduleTemplate.anchor || undefined);
	}

	trackPosterClick({ index, title }: { index: number; title: HorizontalTitleListItem }) {
		const routeName: string = this.activeRoute?.name;

		const selectedTab = this.getSelectedTab(routeName);

		const moduleContext = this.getModuleContext({
			technicalName: 'top-10-titles',
			anchor: '',
			contentType: ModuleContentType.TitlesPerPackage,
		});

		const titleContext = new SnowplowTitleContextGraphql(title.objectId, title.objectType);

		const contexts = [moduleContext, titleContext];

		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'title_clicked',
				label: 'top-10-titles',
				property: selectedTab,
				value: index,
			},
			contexts
		);
	}
}
